import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { roleHome } from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import { useLegalDocument } from '../hooks/useLegalDocument';
import LegalDocumentView from '../components/LegalDocumentView';

// What register() and login() send where a queue ticket used to go. The
// server ignores it — there is no ticket step any more.
const QUEUE_DISABLED_TICKET = 'queue-disabled';

const RegisterPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Set once the account exists, which switches the page over to the
  // "now go and confirm your address" screen. Holds the address so it
  // can be shown back to them — a typo there is otherwise invisible
  // until they wonder why nothing arrived.
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  // Consent to the Data Privacy Policy. Registration is the point at
  // which the programme starts processing personal information, and the
  // profile that follows asks for sensitive categories under RA 10173
  // (ethnic origin, civil status, government ID), so this is a real
  // gate rather than a formality — unticked, the form does not submit.
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const {
    content: privacyPolicy,
    failed: privacyFailed,
    loading: privacyLoading,
    reload: reloadPrivacyPolicy
  } = useLegalDocument('privacy');
  // Loaded but not displayed in full: the tick box agrees to the terms
  // as well, so their version has to travel with the registration or the
  // record would name only half of what was agreed to. The terms
  // themselves stay a link — showing two long documents inline would
  // bury the form.
  const {
    content: terms,
    failed: termsFailed,
    reload: reloadTerms
  } = useLegalDocument('terms');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Checked here rather than by disabling the button: a disabled
    // submit leaves someone clicking a dead control with no explanation
    // of what is missing, which is the complaint the checkbox exists to
    // avoid creating.
    if (!agreedToPrivacy || !privacyPolicy?.version || !terms?.version) {
      setConsentError(true);
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      // Registration always creates a Student account — there is no way
      // to self-select an admin role from this form.
      // The version identifies the exact policy text shown in the box
      // above, so the consent recorded server-side names what was
      // actually read rather than just "the policy".
      await apiService.register(
        firstName,
        lastName,
        email,
        password,
        QUEUE_DISABLED_TICKET,
        privacyPolicy.version,
        terms.version
      );

      // The account exists from here on, so anything that fails below
      // must not read as a failed registration.
      try {
        const user = await login(email, password, QUEUE_DISABLED_TICKET);
        navigate(roleHome[user.role]);
      } catch (signInError: any) {
        // Expected whenever the Super Admin requires a confirmed address:
        // the account is made but cannot be signed into until the link is
        // clicked. Anything else here is unexpected, but the account
        // still exists either way, so both land on the same screen — it
        // tells them what to do next, which "Registration failed" would
        // not.
        //
        // Only the unconfirmed-address case gets the "check your email"
        // screen: while confirmation is switched off no link is sent at
        // all, so telling them to look for one would send them hunting
        // for an email that will never arrive. The account exists, so
        // anything else goes to the sign-in page.
        if (signInError?.code !== 'EMAIL_NOT_VERIFIED') {
          console.error('[Register] Auto sign-in failed after registering', signInError);
          navigate('/login');
          return;
        }
        setRegisteredEmail(email);
        setIsLoading(false);
      }
    } catch (err: any) {
      // The policy was rewritten while this form sat open. Untick and
      // reload it: the tick has to mean agreement to the text on screen,
      // so silently submitting again against the new wording would
      // record a consent that was never given to it.
      if (err?.code === 'PRIVACY_CONSENT_STALE') {
        setAgreedToPrivacy(false);
        // Both, because the server does not say which one moved and
        // either could have.
        reloadPrivacyPolicy();
        reloadTerms();
      }
      setError(err.message || 'Registration failed');
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMessage('');
    try {
      await apiService.resendVerificationEmail(registeredEmail);
      setResendMessage('Sent again. It can take a minute to arrive.');
    } catch (err: any) {
      setResendMessage(err?.message || 'Could not send it again. Please try shortly.');
    } finally {
      setResending(false);
    }
  };

  // The account has been created. Shown in place of the form once it is.
  if (registeredEmail) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <h2>Almost done</h2>
          <p style={{ marginBottom: '0.5rem' }}>
            We sent a confirmation link to <strong>{registeredEmail}</strong>.
          </p>
          <p style={{ marginBottom: '1.25rem' }}>
            Open your email, find the message from ECEAP, and click the link to confirm your account.
          </p>

          {/* Said plainly because it is currently the most likely
              outcome: mail is sent from a gmail.com address through
              another provider, which Gmail treats with suspicion until
              the programme has a domain of its own. */}
          <div
            style={{
              padding: '0.85rem 1rem',
              background: 'var(--warning-bg)',
              border: '1px solid var(--warning-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--warning-text)',
              fontSize: '0.85rem',
              textAlign: 'left',
              marginBottom: '1.25rem'
            }}
          >
            Can't see it? Check your <strong>Spam</strong> or <strong>Junk</strong> folder — and on Gmail, the{' '}
            <strong>Promotions</strong> tab. Marking it "Not spam" helps future messages reach you.
          </div>

          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={resending}
            onClick={handleResend}
            style={{ marginBottom: '0.5rem' }}
          >
            {resending ? 'Sending...' : 'Send the link again'}
          </button>
          {resendMessage && (
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>{resendMessage}</p>
          )}

          <p style={{ marginTop: '1.5rem', marginBottom: 0, fontSize: '0.9rem' }}>
            Already confirmed? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  // The registration form. There is no ticket step any more — see server lib/passwordHashing.ts.
  return (
    <div className="login-container">
      <div className="login-card">
        <h2>Create your student account</h2>
        <p>Register to apply for scholarships and track your application status</p>

        {error && (
          <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        <form className="form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="firstName">First name</label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last name</label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email address</label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
          </div>
          {/* The policy is shown here in full rather than only linked.
              Consent under RA 10173 has to be informed, and a link
              opening a page that loses the half-filled form behind it is
              a link most people will not follow. Scrollable so it states
              the whole thing without burying the submit button. */}
          <div className="form-group">
            <label htmlFor="privacy-policy-box">Data Privacy Policy</label>
            <div
              id="privacy-policy-box"
              tabIndex={0}
              style={{
                maxHeight: '13rem',
                overflowY: 'auto',
                padding: '0.85rem 1rem',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--card-bg)',
                fontSize: '0.8rem'
              }}
            >
              {privacyLoading ? (
                <p style={{ margin: 0 }}>Loading the policy...</p>
              ) : (
                <LegalDocumentView content={privacyPolicy!} headless />
              )}
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>
              Scroll to read it all. It stays available at{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                Data Privacy Policy
              </Link>
              .
            </small>
          </div>

          <div
            className="form-group"
            style={{
              padding: '0.85rem 1rem',
              border: `1px solid ${consentError ? 'var(--error-text)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-md)'
            }}
          >
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={agreedToPrivacy}
                // Blocked when the policy could not be fetched: being
                // asked to agree to a document that failed to load is
                // not informed consent, and recording it as such would
                // be worse than making them reload.
                disabled={privacyFailed || termsFailed}
                onChange={(e) => {
                  setAgreedToPrivacy(e.target.checked);
                  if (e.target.checked) setConsentError(false);
                }}
                style={{ marginTop: '0.2rem', flex: '0 0 auto' }}
              />
              <span style={{ fontSize: '0.85rem' }}>
                I have read and agree to the <strong>Data Privacy Policy</strong> above, and to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer">
                  Terms and Conditions
                </Link>
                .
              </span>
            </label>

            {(privacyFailed || termsFailed) && (
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: 'var(--error-text)' }}>
                {privacyFailed ? 'The policy' : 'The Terms and Conditions'} could not be loaded, so the
                agreement cannot be accepted yet. Please reload the page.
              </p>
            )}
            {consentError && !privacyFailed && !termsFailed && (
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: 'var(--error-text)' }} role="alert">
                You must agree to the Data Privacy Policy to continue
              </p>
            )}
          </div>

          <button className="btn btn-primary btn-lg" type="submit" disabled={isLoading} style={{ width: '100%' }}>
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <p style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.9rem' }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
